import { Dayjs } from 'dayjs';

import { SDate, SDuration } from '@summit/core';

import { MileageFormatter, PadAround, PadLeft, PadRight } from '@summit/models';
import { EboObjectTO } from '../base-classes/ebo-object-to';
import { Hub } from '../places/hub/hub';
import { Truck } from '../truck/truck';
import { MaintEvent } from './maint-event';
import { MaintPlanItem } from './maint-plan-item';

export class MaintStatus extends EboObjectTO {
  truckID: string;
  truck: Truck;
  maintPlanItemId: string;
  maintPlanItem: MaintPlanItem;
  lastServiceDate: Dayjs;
  lastServiceOdometer: number;
  maintEventID: string;
  maintEvent: MaintEvent;

  //For Styling
  currentOdometer: number;
  weekMileageEstimate: number;
  estServiceDateString: string;
  estDurationTilString: string;
  private _showDistanceFlag: boolean;
  private _showDistanceWarn: boolean;
  private _showDistanceAlarm: boolean;
  private _showDurationFlag: boolean;
  private _showDurationWarn: boolean;
  private _showDurationAlarm: boolean;

  constructor(jsonData?: Partial<MaintStatus>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.lastServiceDate) this.lastServiceDate = SDate.newUtc(jsonData.lastServiceDate);
      if (jsonData.truck) {
        this.truck = new Truck(jsonData.truck);
      }
      if (jsonData.maintPlanItem) {
        this.maintPlanItem = new MaintPlanItem(jsonData.maintPlanItem);
      }
      if (jsonData.maintEvent) {
        this.maintEvent = new MaintEvent(jsonData.maintEvent);
      }
    }
  }

  isDurationBased(): boolean {
    if (this.maintPlanItem?.duration?.asDays() > 0 || this.maintPlanItem?.item?.duration?.asDays() > 0) {
      return true;
    }
    return false;
  }
  isMileageBased(): boolean {
    if (this.maintPlanItem?.mileage > 0 || this.maintPlanItem?.item?.mileage > 0) {
      return true;
    }
    return false;
  }

  private _truckHeader: string;
  get truckHeader(): string {
    if (this._truckHeader === undefined) {
      const response = [];
      if (this.truck) {
        if (this.truck.activeAliasAssignments && this.truck.activeAliasAssignments.length > 0) {
          response.push(`${this.truck.displayName}:`);
          response.push(
            this.truck.activeAliasAssignments
              .map((alias) => alias.alias.name)
              .sort()
              .join('/')
          );
        } else {
          response.push(this.truck.displayName);
        }
      }
      if (this.currentOdometer) {
        response.push(`(${MileageFormatter(this.currentOdometer)})`);
      }
      this._truckHeader = response.join(' ');
      if (this._truckHeader === '') {
        this._truckHeader = null;
      }
    }
    return this._truckHeader;
  }

  get nextServiceDate(): Dayjs {
    if (this.isDurationBased() && this.lastServiceDate) {
      return this.lastServiceDate.add(this.maintPlanItem.duration ?? this.maintPlanItem?.item?.duration);
    }
    return null;
  }

  get nextServiceOdometer(): number {
    if (this.maintPlanItem?.mileage || this.maintPlanItem?.item?.mileage) {
      return (this.lastServiceOdometer ?? 0) + (this.maintPlanItem.mileage ?? this.maintPlanItem?.item?.mileage ?? 0);
    }
    return null;
  }

  get defaultSort(): string {
    return `${this.truck.name} - ${this.maintPlanItem.item?.name}`;
  }

  get remainingMiles(): number {
    if (this.nextServiceOdometer) return this.nextServiceOdometer - (this.currentOdometer ?? 0);
    return null;
  }

  get remainingDays(): number {
    if (this.nextServiceDate) return this.nextServiceDate.diff(SDate.new(), 'days');
    return null;
  }

  get hub(): Partial<Hub> {
    return this.truck?.hub ?? { id: 0, name: 'Unknown' };
  }

  clearCache(): void {
    this._showDistanceAlarm = undefined;
    this._showDistanceFlag = undefined;
    this._showDistanceWarn = undefined;
    this._showDurationAlarm = undefined;
    this._showDurationFlag = undefined;
    this._showDurationWarn = undefined;
  }

  isEmpty(): boolean {
    return !this.lastServiceDate && !this.lastServiceOdometer;
  }

  showDurationAlarm(): boolean {
    if (this._showDurationAlarm == undefined) {
      if (this.isDurationBased()) {
        const timeWindow =
          this.maintPlanItem.durationThresholdAlarm ?? this.maintPlanItem?.item?.durationThresholdAlarm;
        const windowEnd = SDate.new().add(timeWindow);
        if (windowEnd >= this.nextServiceDate) {
          this._showDurationAlarm = true;
        }
      }
      this._showDurationAlarm ??= false;
    }
    return this._showDurationAlarm;
  }
  showDistanceAlarm(): boolean {
    if (this._showDistanceAlarm == undefined) {
      if (this.isMileageBased()) {
        const mileageDelta =
          this.maintPlanItem.mileageThresholdAlarm ?? this.maintPlanItem?.item?.mileageThresholdAlarm;
        if (this.currentOdometer >= this.nextServiceOdometer - mileageDelta) {
          this._showDistanceAlarm = true;
        }
      }
      this._showDistanceAlarm ??= false;
    }
    return this._showDistanceAlarm;
  }

  showDurationWarn(): boolean {
    if (this._showDurationWarn == undefined) {
      if (!this._showDurationWarn && this.isDurationBased() && this.lastServiceDate) {
        const timeWindow = this.maintPlanItem.durationThresholdWarn ?? this.maintPlanItem?.item?.durationThresholdWarn;
        //console.log(timeWindow.asDays());
        const windowEnd = SDate.new().add(timeWindow);
        //if (this.truckID == '7a3d6816-e744-460c-bc63-a9bf66af4b0b') console.log('testing', windowEnd);
        if (windowEnd >= this.nextServiceDate) {
          this._showDurationWarn = true;
        }
      }
      this._showDurationWarn ??= false;
    }
    return this._showDurationWarn;
  }
  showDistanceWarn(): boolean {
    if (this._showDistanceWarn == undefined) {
      if (this.isMileageBased()) {
        const mileageDelta = this.maintPlanItem.mileageThresholdWarn ?? this.maintPlanItem?.item?.mileageThresholdWarn;
        if (this.currentOdometer >= this.nextServiceOdometer - mileageDelta) {
          this._showDistanceWarn = true;
        }
      }
      this._showDistanceWarn ??= false;
    }
    return this._showDistanceWarn;
  }

  showDurationFlag(): boolean {
    if (this._showDurationFlag == undefined) {
      if (this.isDurationBased() && this.lastServiceDate) {
        const timeWindow = this.maintPlanItem.durationThresholdFlag ?? this.maintPlanItem?.item?.durationThresholdFlag;
        //console.log(timeWindow.asDays());
        const windowEnd = SDate.new().add(timeWindow);
        //if (this.truckID == '7a3d6816-e744-460c-bc63-a9bf66af4b0b') console.log('testing', windowEnd);
        if (windowEnd >= this.nextServiceDate) {
          this._showDurationFlag = true;
        }
      }
      this._showDurationFlag ??= false;
    }
    return this._showDurationFlag;
  }
  showDistanceFlag(): boolean {
    if (this._showDistanceFlag == undefined) {
      if (this.isMileageBased()) {
        const mileageDelta = this.maintPlanItem.mileageThresholdFlag ?? this.maintPlanItem?.item?.mileageThresholdFlag;
        if (this.currentOdometer >= this.nextServiceOdometer - mileageDelta) {
          this._showDistanceFlag = true;
        }
      }
      this._showDistanceFlag ??= false;
    }
    return this._showDistanceFlag;
  }

  buildEstimate() {
    if (this.isMileageBased() && this.remainingMiles < 0) {
      this.estDurationTilString = 'ASAP';
      this.estServiceDateString = 'ASAP';
      return;
    }
    if (this.weekMileageEstimate) {
      var remainingWeeks = this.remainingMiles / this.weekMileageEstimate;
      var estimatedDate = SDate.new().add(remainingWeeks, 'week');
      this.estServiceDateString = estimatedDate.format('M/D/YY');
      var estDuration = SDuration.new(estimatedDate.diff(SDate.new()));
      this.estDurationTilString = estDuration.humanize();
    } else {
      this.estServiceDateString = 'TBD';
      this.estDurationTilString = 'TBD';
    }
  }

  toExcelString(): string {
    var response = '';
    response += `<b>${PadRight('Last:', 6)}</b>`;

    var durationBackgroundColor = this.showDistanceAlarm()
      ? 'background-color: red;'
      : this.showDistanceWarn()
        ? 'background-color: orange;'
        : this.showDistanceFlag()
          ? 'background-color: blue;'
          : '';
    if (this.lastServiceOdometer >= 626064 && this.lastServiceOdometer <= 626065) {
      console.warn(
        `Last Service Odometer for ${this.truck?.name} - ${this.maintPlanItem?.item?.name} is ${this.lastServiceOdometer}`,
        this.showDistanceAlarm(),
        this.showDistanceWarn(),
        this.showDistanceFlag()
      );
    }
    var distanceBackgroundColor = this.showDistanceAlarm()
      ? 'background-color: red;'
      : this.showDistanceWarn()
        ? 'background-color: orange;'
        : this.showDistanceFlag()
          ? 'background-color: blue;'
          : '';
    if (this.lastServiceDate) {
      response += `<div style="${durationBackgroundColor}">${PadAround(this.lastServiceDate?.format('MM/DD/YY'), 10)}</div>`;
    }
    if (this.lastServiceOdometer) {
      response += `<div style="${distanceBackgroundColor}">${PadLeft(MileageFormatter(this.lastServiceOdometer), 10)}</div>`;
    }
    response += '<br/>';
    response += `<b>${PadRight('Next:', 6)}</b>`;
    if (this.nextServiceDate) {
      response += `<div style="${durationBackgroundColor}">${PadAround(this.nextServiceDate?.format('MM/DD/YY'), 10)}</div>`;
    } else if (this.estServiceDateString) {
      response += `<div style="${durationBackgroundColor}">${PadAround(this.estServiceDateString, 10)}</div>`;
    }
    if (this.nextServiceOdometer) {
      response += `<div style="${distanceBackgroundColor}">${PadLeft(MileageFormatter(this.nextServiceOdometer), 10)}</div>`;
    }
    response += '<br/>';
    response += `<b>${PadRight('Left:', 6)}</b>`;
    if (this.remainingDays) {
      response += `<div style="${durationBackgroundColor}">${PadAround(this.remainingDays.toString(), 10)}</div>`;
    } else if (this.estDurationTilString) {
      response += `<div style="${durationBackgroundColor}">${PadAround(this.estDurationTilString, 10)}</div>`;
    }
    if (this.remainingMiles) {
      response += `<div style="${distanceBackgroundColor}">${PadLeft(MileageFormatter(this.remainingMiles), 10)}</div>`;
    }
    response += '<br/>';
    return response;
  }
}
